import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect
} from 'react';
import {
  getSelf,
  getUserInfo,
  logout as serviceLogout
} from '../services/auth.service';
import { UserInfoModel } from '../models/api';
import { FeatureEnum } from '../components/FeatureGate';

interface AuthContextType {
  userInfo: UserInfoModel | null;
  logout: () => Promise<void>;
  refresh: () => void;
  isLoggedIn: boolean;
  isAdmin: boolean;
  loading: boolean;
  clearData: () => void;
  accountLevel: string | null;
  features: string[];
  hasFeature: (feature: FeatureEnum) => boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [userInfo, setUserInfo] = useState(getUserInfo());
  const [accountLevel, setAccountLevel] = useState<string | null>(null);
  const [features, setFeatures] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await getSelf();

        if (res.success && res.data) {
          const data: UserInfoModel = res.data;
          setUserInfo(data);
          setAccountLevel(data.accountLevel);
          setFeatures(data.features ?? []);
        } else {
          setUserInfo(null);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        setUserInfo(null);
        setAccountLevel(null);
        setFeatures([]);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const logout = async () => {
    await serviceLogout();
    setUserInfo(null);
    setAccountLevel(null);
    setFeatures([]);
  };
  const clearData = async () => {
    setUserInfo(null);
    setAccountLevel(null);
    setFeatures([]);
  };

  const refresh = () => {
    const user = getUserInfo();
    setUserInfo(user);
    setAccountLevel(user?.accountLevel ?? null);
    setFeatures(user?.features ?? []);
  };

  const hasFeature = (feature: FeatureEnum) => features.includes(feature);

  const isLoggedIn = !!userInfo;
  const isAdmin = userInfo?.roles?.includes('Admin') ?? false;

  return (
    <AuthContext.Provider
      value={{
        userInfo,
        logout,
        refresh,
        isLoggedIn,
        isAdmin,
        clearData,
        loading,
        accountLevel,
        features,
        hasFeature
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
